import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { ChevronRight, ChevronLeft, Brain, Loader2, CheckCircle2 } from "lucide-react";
import { ALL_QUESTIONS, SECTION_META, Question } from "@/data/assessment-questions";
import { computeTraitProfile, getTopTraits, getClusterAverages, getDominantCluster } from "@/utils/traitScoring";
import { StorageService } from "@/hooks/services/storageService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { StudentStore } from "@/hooks/stores/StudentStore";
import api from "@/hooks/api/apiClient";
import config from "../../../config";
import { AssessmentOption } from "@/components/ui/assessments/assessment-option";
import { AssessmentNavigation } from "@/components/ui/assessments/assessment-navigation";
import { AssessmentQuestion } from "@/components/ui/assessments/assessment-question";
import { useTraitMutation } from "@/hooks/useTraitMutation";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type Answers = Record<string, string>; // questionId → optionId
type Phase = "welcome" | "section-intro" | "question" | "computing" | "results";

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------
const ProgressBar = ({ answered, total }: { answered: number; total: number }) => (
  <div className="w-full bg-white/30 rounded-full h-1.5">
    <div
      className="bg-white h-1.5 rounded-full transition-all duration-500"
      style={{ width: `${Math.round((answered / total) * 100)}%` }}
    />
  </div>
);

// ---------------------------------------------------------------------------
// Progress persistence helpers
// ---------------------------------------------------------------------------
const PSYCHOMETRIC_SAVE_KEY = "es_progress_psychometric";

function saveProgress(currentIndex: number, answers: Answers, seenSections: number[]) {
  try {
    sessionStorage.setItem(PSYCHOMETRIC_SAVE_KEY, JSON.stringify({ currentIndex, answers, seenSections }));
  } catch (_) {}
}

function loadProgress(): { currentIndex: number; answers: Answers; seenSections: number[] } | null {
  try {
    const raw = sessionStorage.getItem(PSYCHOMETRIC_SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.currentIndex !== "number" || typeof parsed.answers !== "object") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function clearProgress() {
  try { sessionStorage.removeItem(PSYCHOMETRIC_SAVE_KEY); } catch (_) {}
}

// ---------------------------------------------------------------------------
// Welcome screen
// ---------------------------------------------------------------------------
const WelcomeScreen = ({ onStart, onSkip, onResume, resumeCount }: { onStart: () => void; onSkip: () => void; onResume?: () => void; resumeCount?: number }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 px-6 text-center">
    <div className="max-w-lg space-y-8">
      <div className="w-20 h-20 rounded-2xl bg-indigo-100 border border-indigo-200 flex items-center justify-center mx-auto shadow-sm">
        <Brain className="w-10 h-10 text-indigo-600" />
      </div>

      <div className="space-y-3">
        <p className="text-indigo-600 text-sm font-semibold tracking-widest uppercase">Before you begin</p>
        <h1 className="text-4xl font-bold text-slate-900">Discover Your Strengths</h1>
        <p className="text-slate-500 text-lg leading-relaxed">
          This short assessment helps us understand how you naturally think, feel, and work — so we can match you to career paths that are a genuine fit.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 text-left shadow-sm">
        {[
          ["32 questions", "Split across three short sections"],
          ["No wrong answers", "Every option reflects a valid strength"],
          ["~8 minutes", "Go at your own pace, take your time"],
        ].map(([label, desc]) => (
          <div key={label} className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 shrink-0" />
            <div>
              <span className="text-slate-800 font-semibold">{label} — </span>
              <span className="text-slate-500">{desc}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {onResume && resumeCount != null && resumeCount > 0 ? (
          <>
            <button
              onClick={onResume}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-md flex items-center justify-center gap-2 text-lg"
            >
              Resume ({resumeCount} / {ALL_QUESTIONS.length} answered) <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={onStart}
              className="w-full py-3 border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-700 bg-white rounded-xl transition-all text-sm"
            >
              Start fresh
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onStart}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-md flex items-center justify-center gap-2 text-lg"
            >
              Start Assessment <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={onSkip}
              className="w-full py-3 border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-700 bg-white rounded-xl transition-all text-sm"
            >
              Skip — auto-fill for demo
            </button>
          </>
        )}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Section intro screen
// ---------------------------------------------------------------------------
const SECTION_COLORS: Record<number, { bg: string; btn: string }> = {
  1: { bg: "bg-blue-600",  btn: "bg-blue-600 hover:bg-blue-700" },
  2: { bg: "bg-rose-500",  btn: "bg-rose-500 hover:bg-rose-600" },
  3: { bg: "bg-violet-600",btn: "bg-violet-600 hover:bg-violet-700" },
};

const SectionIntroScreen = ({
  section,
  onContinue,
}: {
  section: 1 | 2 | 3;
  onContinue: () => void;
}) => {
  const meta = SECTION_META[section];
  const colors = SECTION_COLORS[section] ?? SECTION_COLORS[1];

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 px-6 text-center">
      <div className="max-w-md space-y-8">
        <div className={`w-16 h-16 rounded-2xl ${colors.bg} flex items-center justify-center mx-auto shadow-md`}>
          <span className="text-white text-2xl font-black">{section}</span>
        </div>
        <div className="space-y-2">
          <p className="text-slate-400 text-sm tracking-widest uppercase font-semibold">Section {section} of 3</p>
          <h2 className="text-3xl font-bold text-slate-900">{meta.title}</h2>
          <p className="text-slate-500 text-base">{meta.subtitle}</p>
        </div>
        <button
          onClick={onContinue}
          className={`w-full py-4 ${colors.btn} text-white font-bold rounded-xl transition-all shadow-md flex items-center justify-center gap-2`}
        >
          Let&apos;s Go <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Single question card
// ---------------------------------------------------------------------------
const QuestionCard = ({
  question,
  selectedOption,
  onSelect,
  questionNumber,
  totalQuestions,
  onNext,
  onBack,
  canGoBack,
  isAnimating,
}: {
  question: Question;
  selectedOption: string | null;
  onSelect: (optionId: string) => void;
  questionNumber: number;
  totalQuestions: number;
  onNext: () => void;
  onBack: () => void;
  canGoBack: boolean;
  isAnimating: boolean;
}) => {
  const meta = SECTION_META[question.section];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Coloured section header */}
      <div className={`${SECTION_COLORS[question.section as 1|2|3]?.bg ?? "bg-indigo-600"} px-6 pt-6 pb-4`}>
        <div className="max-w-2xl mx-auto space-y-2">
          <div className="flex items-center justify-between text-white/80 text-sm">
            <span className="font-semibold">{meta.title}</span>
            <span>{questionNumber} / {totalQuestions}</span>
          </div>
          <ProgressBar answered={questionNumber - 1} total={totalQuestions} />
        </div>
      </div>

      {/* Question + options */}
      <div className="flex-1 flex flex-col justify-center px-6 py-8">
        <div className="max-w-2xl mx-auto w-full bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-6">
          <AssessmentQuestion text={question.text} isAnimating={isAnimating} />

          <div className="space-y-3">
            {question.options.map((option) => (
              <AssessmentOption
                key={option.id}
                label={option.text}
                value={option.id}
                isSelected={selectedOption === option.id}
                isAnimating={isAnimating}
                onClick={onSelect}
              />
            ))}
          </div>

          <AssessmentNavigation
            onPrevious={onBack}
            onNext={onNext}
            canGoPrevious={canGoBack}
            canGoNext={!!selectedOption}
            isAnimating={isAnimating}
            isLastQuestion={questionNumber === totalQuestions}
            currentQuestion={questionNumber}
            totalQuestions={totalQuestions}
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Computing screen
// ---------------------------------------------------------------------------
const ComputingScreen = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 px-6 text-center space-y-6">
    <Loader2 className="w-12 h-12 text-indigo-500 animate-spin" />
    <div className="space-y-2">
      <h2 className="text-2xl font-bold text-slate-900">Building your profile…</h2>
      <p className="text-slate-500">Analysing your responses across all 49 traits</p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Results screen
// ---------------------------------------------------------------------------
const CLUSTER_META: Record<string, { label: string; color: string; bg: string }> = {
  STEM:        { label: "STEM & Technology",     color: "text-blue-700",    bg: "bg-blue-50 border-blue-200" },
  Humanities:  { label: "Arts & Humanities",     color: "text-rose-700",    bg: "bg-rose-50 border-rose-200" },
  Management:  { label: "Business & Leadership", color: "text-amber-700",   bg: "bg-amber-50 border-amber-200" },
  all:         { label: "Universal Skills",      color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
};

import traitList from "../../../traitList.json";

const TraitBar = ({ traitId, score }: { traitId: string; score: number }) => {
  const trait = (traitList as Array<{ id: string; full_name: string }>).find((t) => t.id === traitId);
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-sm text-slate-700">{trait?.full_name ?? traitId}</span>
        <span className="text-sm font-bold text-slate-900">{score}</span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2">
        <div
          className="bg-linear-to-r from-indigo-500 to-violet-500 h-2 rounded-full transition-all duration-700"
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
};

const ResultsScreen = ({
  profile,
  dominantCluster,
  clusterAverages,
  topTraits,
  onContinue,
}: {
  profile: Record<string, number>;
  dominantCluster: string;
  clusterAverages: Record<string, number>;
  topTraits: Array<{ traitId: string; score: number }>;
  onContinue: () => void;
}) => {
  const dominant = CLUSTER_META[dominantCluster] ?? CLUSTER_META.STEM;

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-12">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mx-auto shadow-sm">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Your Trait Profile is Ready</h1>
          <p className="text-slate-500">
            Your strongest natural cluster is{" "}
            <span className={`font-bold ${dominant.color}`}>{dominant.label}</span>
          </p>
        </div>

        {/* Cluster averages */}
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(clusterAverages).map(([cluster, avg]) => {
            const meta = CLUSTER_META[cluster] ?? CLUSTER_META.STEM;
            return (
              <div key={cluster} className={`border rounded-xl p-4 bg-white shadow-sm ${meta.bg}`}>
                <p className={`text-xs font-semibold uppercase tracking-wider ${meta.color}`}>{meta.label}</p>
                <p className="text-3xl font-black text-slate-900 mt-1">{avg}</p>
                <p className="text-slate-400 text-xs">/ 100 avg</p>
              </div>
            );
          })}
        </div>

        {/* Top 5 traits */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 shadow-sm">
          <h3 className="text-slate-900 font-bold text-lg">Your Top 5 Traits</h3>
          {topTraits.map(({ traitId, score }) => (
            <TraitBar key={traitId} traitId={traitId} score={score} />
          ))}
        </div>

        <p className="text-center text-slate-400 text-sm">
          All 49 traits have been scored and saved to your profile. Your career matches will be refined over time as you complete courses and assessments.
        </p>

        <button
          onClick={onContinue}
          className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-md flex items-center justify-center gap-2 text-lg"
        >
          View My Profile <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main assessment page
// ---------------------------------------------------------------------------
export default function PsychometricAssessment() {
  const navigate = useNavigate();
  const { applyEvent } = useTraitMutation() as any; // access internal applyEvent for retake path

  // Load any saved progress once at mount
  const savedProgress = useRef(loadProgress());

  const [phase, setPhase] = useState<Phase>("welcome");
  const [currentIndex, setCurrentIndex] = useState(savedProgress.current?.currentIndex ?? 0);
  const [answers, setAnswers] = useState<Answers>(savedProgress.current?.answers ?? {});
  const [isAnimating, setIsAnimating] = useState(false);
  const [results, setResults] = useState<{
    profile: Record<string, number>;
    topTraits: Array<{ traitId: string; score: number }>;
    clusterAverages: Record<string, number>;
    dominantCluster: string;
  } | null>(null);

  // Detect first-timer once at mount — never changes mid-session
  const isFirstTimerRef = useRef<boolean>(
    StudentStore.getState().student?.traitProfile == null
  );

  const currentQuestion = ALL_QUESTIONS[currentIndex];
  const currentSection = currentQuestion?.section;

  // Determine which sections have been "intro'd" already
  const [seenSections, setSeenSections] = useState<Set<number>>(
    new Set(savedProgress.current?.seenSections ?? [])
  );

  // Auto-save progress whenever answers or position changes during the question phase
  useEffect(() => {
    if (phase === "question" || phase === "section-intro") {
      saveProgress(currentIndex, answers, [...seenSections]);
    }
  }, [currentIndex, answers, seenSections, phase]);

  const handleStart = () => {
    clearProgress();
    savedProgress.current = null;
    setCurrentIndex(0);
    setAnswers({});
    setSeenSections(new Set());
    setPhase("section-intro");
  };

  const handleResume = () => {
    // State is already initialised from savedProgress — jump straight to question
    setPhase(seenSections.has(ALL_QUESTIONS[currentIndex]?.section ?? 1) ? "question" : "section-intro");
  };

  const handleSkip = () => {
    clearProgress();
    const randomAnswers: Answers = {};
    ALL_QUESTIONS.forEach((q) => {
      const opts = q.options;
      randomAnswers[q.id] = opts[Math.floor(Math.random() * opts.length)].id;
    });
    setAnswers(randomAnswers);
    finishAssessment(randomAnswers);
  };

  const handleSectionIntroContinue = () => {
    setSeenSections((prev) => new Set([...prev, currentSection]));
    setPhase("question");
  };

  const handleSelectOption = (optionId: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: optionId }));
  };

  const handleNext = () => {
    const nextIndex = currentIndex + 1;

    if (nextIndex >= ALL_QUESTIONS.length) {
      // All questions answered — compute
      finishAssessment();
      return;
    }

    const nextQuestion = ALL_QUESTIONS[nextIndex];
    const nextSection = nextQuestion.section;

    setCurrentIndex(nextIndex);

    if (nextSection !== currentSection && !seenSections.has(nextSection)) {
      setPhase("section-intro");
    }
    // else stay in "question" phase
  };

  const handleBack = () => {
    if (currentIndex === 0) return;
    setCurrentIndex((prev) => prev - 1);
    setPhase("question");
  };

  const finishAssessment = (answerOverride?: Answers) => {
    clearProgress();
    setPhase("computing");

    setTimeout(() => {
      const newProfile = computeTraitProfile(answerOverride ?? answers);
      const topTraits = getTopTraits(newProfile, 5);
      const clusterAverages = getClusterAverages(newProfile);
      const dominantCluster = getDominantCluster(newProfile);

      setResults({ profile: newProfile, topTraits, clusterAverages, dominantCluster });

      if (isFirstTimerRef.current) {
        // First time — write the full profile directly
        persistTraitProfileFirstTime(newProfile);
      } else {
        // Retake — compute deltas and run through mutation engine
        persistTraitProfileRetake(newProfile);
      }

      setPhase("results");
    }, 2000);
  };

  const persistTraitProfileFirstTime = (profile: Record<string, number>) => {
    try {
      const currentUser = AuthStore.getState().currentUser;
      if (!currentUser) return;

      // Update Zustand store
      StudentStore.getState().setTraitProfile(profile);
      StudentStore.getState().updateMetadata({ lastAssessmentDate: new Date().toISOString() });

      // Keep prototype localStorage in sync
      if (config.appMode === "prototype") {
        const students: any[] = StorageService.read("students");
        const idx = students.findIndex((s) => s.userId === currentUser._id || s.email === currentUser.email);
        if (idx !== -1) {
          students[idx] = { ...students[idx], traitProfile: profile, updatedAt: new Date() };
          StorageService.write("students", students);
        }
      } else {
        api.post("/students/me/career-assessment", { traitProfile: profile })
          .catch((err) => console.error("[Assessment] save failed:", err));
      }
    } catch (err) {
      console.error("Failed to persist trait profile:", err);
    }
  };

  const persistTraitProfileRetake = (newProfile: Record<string, number>) => {
    try {
      const currentUser = AuthStore.getState().currentUser;
      if (!currentUser) return;
      const students: any[] = StorageService.read("students");
      const idx = students.findIndex((s) => s.userId === currentUser._id || s.email === currentUser.email);
      if (idx === -1) return;
      const currentProfile: Record<string, number> = students[idx].traitProfile ?? {};

      // Build delta: new - old per trait
      const traitDeltas: Record<string, number> = {};
      for (const traitId of Object.keys(newProfile)) {
        const delta = newProfile[traitId] - (currentProfile[traitId] ?? 0);
        if (delta !== 0) traitDeltas[traitId] = delta;
      }
      if (Object.keys(traitDeltas).length === 0) return;

      // Emit as a psychometric TraitEvent — mutation engine applies source weight ×3.0,
      // diminishing returns, ±12 clamp, and runs CSE if active nodes exist
      const event = {
        id: `evt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        studentId: currentUser._id ?? "",
        source: "psychometric" as const,
        sourceId: `retake_${Date.now()}`,
        sourceName: "Psychometric Assessment (Retake)",
        traitDeltas,
        timestamp: new Date().toISOString(),
        meta: {
          label: "Psychometric Retake",
          lastAssessmentDate: new Date().toISOString(),
        },
      };
      applyEvent(event);

      // Update Zustand store with new metadata
      StudentStore.getState().updateMetadata({ lastAssessmentDate: new Date().toISOString() });

      // Keep prototype localStorage in sync
      if (config.appMode === "prototype") {
        const freshStudents: any[] = StorageService.read("students");
        const freshIdx = freshStudents.findIndex((s) => s.userId === currentUser._id || s.email === currentUser.email);
        if (freshIdx !== -1) {
          freshStudents[freshIdx] = {
            ...freshStudents[freshIdx],
            metadata: { ...(freshStudents[freshIdx].metadata ?? {}), lastAssessmentDate: new Date().toISOString() },
          };
          StorageService.write("students", freshStudents);
        }
      } else {
        const updatedProfile = StudentStore.getState().student?.traitProfile ?? {};
        api.post("/students/me/career-assessment", { traitProfile: updatedProfile, traitEvents: [event] })
          .catch((err) => console.error("[Assessment] retake save failed:", err));
      }
    } catch (err) {
      console.error("Failed to persist retake trait profile:", err);
    }
  };

  const handleContinueToDashboard = () => {
    if (isFirstTimerRef.current) {
      // First-timer: pass traitProfile so path discovery has it available
      navigate("/assessment-results?first=true", { state: { traitProfile: results?.profile ?? {} } });
    } else {
      navigate("/assessment-results");
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (phase === "welcome") {
    const resumeCount = savedProgress.current ? Object.keys(savedProgress.current.answers).length : 0;
    return (
      <WelcomeScreen
        onStart={handleStart}
        onSkip={handleSkip}
        onResume={resumeCount > 0 ? handleResume : undefined}
        resumeCount={resumeCount}
      />
    );
  }

  if (phase === "section-intro" && currentSection) {
    return (
      <SectionIntroScreen
        section={currentSection as 1 | 2 | 3}
        onContinue={handleSectionIntroContinue}
      />
    );
  }

  if (phase === "question" && currentQuestion) {
    // Count questions in current section for the progress label
    const sectionQuestions = ALL_QUESTIONS.filter((q) => q.section === currentSection);
    const sectionStart = ALL_QUESTIONS.findIndex((q) => q.section === currentSection);
    const questionNumberInSection = currentIndex - sectionStart + 1;

    return (
      <QuestionCard
        question={currentQuestion}
        selectedOption={answers[currentQuestion.id] ?? null}
        onSelect={handleSelectOption}
        questionNumber={currentIndex + 1}
        totalQuestions={ALL_QUESTIONS.length}
        onNext={handleNext}
        onBack={handleBack}
        canGoBack={currentIndex > 0}
        isAnimating={isAnimating}
      />
    );
  }

  if (phase === "computing") {
    return <ComputingScreen />;
  }

  if (phase === "results" && results) {
    return (
      <ResultsScreen
        profile={results.profile}
        dominantCluster={results.dominantCluster}
        clusterAverages={results.clusterAverages}
        topTraits={results.topTraits}
        onContinue={handleContinueToDashboard}
      />
    );
  }

  return null;
}
